import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Autocomplete } from '@/modules/common';

import { eventAiOptions } from '../../../constants';
import { EventSystemPromptResponse } from '../../../types';
import { useSystemPromptSection } from './hooks/use-system-prompt-section';

interface AIConfigurationTabProps {
  eventSystemPrompt: EventSystemPromptResponse | null;
}

export const AIConfigurationTab = ({
  eventSystemPrompt,
}: AIConfigurationTabProps) => {
  const { control } = useFormContext();

  const {
    selectedCategoryId,
    onCategoryChange,
    categoryOptions,
    promptOptions,
    showContentFields,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useSystemPromptSection(eventSystemPrompt);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <Autocomplete
          name="aiModel"
          label="AI Model"
          control={control}
          placeholder="Select AI model"
          options={eventAiOptions}
          requiredMark
        />
        <div className="gap-3 flex flex-col">
          <h3 className="text-sm font-semibold text-zinc-50">Security Prompt</h3>
          <FormField
            control={control}
            name="gptEnabled"
            render={({ field }) => (
              <FormControl>
                <div className="border p-2 h-[44px] border-zinc-800 rounded-md">
                  <label
                    htmlFor="gptEnabled"
                    className="cursor-pointer flex items-center gap-2 text-zinc-100"
                  >
                    <span
                      className={cn(
                        'text-sm mr-auto',
                        field.value ? 'text-zinc-50' : 'text-zinc-400'
                      )}
                    >
                      {field.value ? 'Enable' : 'Disable'}
                    </span>
                    <Switch
                      id="gptEnabled"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </label>
                </div>
              </FormControl>
            )}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-zinc-50">Security Prompt</h3>
        <FormField
          control={control}
          name="securityPrompt"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  className="min-h-[200px] font-mono text-sm"
                  placeholder="Enter security prompt..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-zinc-50">System Prompt</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Autocomplete
            name="systemPromptCategoryId"
            label="Category"
            control={control}
            placeholder="Select category"
            options={categoryOptions}
            onChange={onCategoryChange}
          />
          <Autocomplete
            name="systemPromptId"
            label="System Prompt"
            control={control}
            placeholder="Select prompt"
            options={promptOptions}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </div>

        {showContentFields && (
          <div className="flex flex-col gap-4">
            <FormField
              control={control}
              name="systemPromptContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[200px] font-mono text-sm"
                      placeholder="Enter prompt content..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="systemPromptSurpriseMeContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Surprise Me Content</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[200px] font-mono text-sm"
                      placeholder="Enter surprise me content..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="systemPromptNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add notes..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};
